import breadcrumbs from 'assets/img/breadcrumbs.svg';
import classNames from 'classnames';
import { Alert } from 'components/Alert';
import { FileSizeLabel } from './FileSizeLabel';
import Button from 'components/Button/Button';
import LoaderWithProps from 'components/Loader/LoaderWithProps';
import { useAdditionalValidation } from 'components/WithAdditionalValidation/useAdditionalValidation';
import { TFileId } from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import { createAppError } from 'utils/helpers';
import {
	FormImageUploaderNewContext,
	IFormImageUploaderNewContext,
	ILoadFromFilePathOptions,
	IMaxFileSizeMB,
} from '../formImageUploaderNewState/formImageUploaderNewContext';
import Text from 'components/Text/Text';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { getActualFilePath } from 'backend/additionalInformation';
import style from './Uploader.module.scss';

interface IProps {
	fileId: TFileId;
	deletePhotoHandler: () => void;
	containerClass?: string;
	noImageIconId?: string;
	loadFromFilePathOptions?: ILoadFromFilePathOptions;
	withValidation?: boolean;
	isRequired?: boolean;
	validationErrorTranslationKey?: string;
	validationFieldName?: string;
	infoContent?: React.ReactNode;
	maxFileSizeMB: IMaxFileSizeMB;
}

export const Uploader: React.FC<IProps> = ({
	fileId,
	containerClass,
	noImageIconId = 'menu',
	loadFromFilePathOptions,
	deletePhotoHandler,
	withValidation = false,
	isRequired = true,
	validationErrorTranslationKey = 'uploadIconNecessary',
	validationFieldName = 'addingIcon',
	infoContent,
	maxFileSizeMB,
}) => {
	const { addAppError } = useContext<IAppContext>(AppContext);
	const {
		setLoading,
		changeTypeOfModalWindow,
		setCroppedSizeSrc,
		setPendingImage,
		setPendingGif,
		setLoadingImageError,
		isLoading,
		croppedSizeSrc,
		loadingImageError,
	} = useContext<IFormImageUploaderNewContext>(FormImageUploaderNewContext);
	const [actualPreSignedUrl, setActualPreSignedUrl] = useState<string | null>(null);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const isValidationSatisfied = !withValidation || !isRequired || !!(fileId || loadFromFilePathOptions?.filePath);
	const { isShowValidationError, validationErrorMessage } = useAdditionalValidation(
		isValidationSatisfied,
		validationErrorTranslationKey,
		validationFieldName,
	);

	useEffect(() => {
		if (withValidation && isShowValidationError) {
			containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, [isShowValidationError]);

	const containerClasses = classNames(style.container, containerClass);
	const deleteIconClasses = classNames(style.icon, style.deleteIcon);
	const deleteButtonClasses = classNames(style.deleteButton, {
		[style.buttonVisible]: croppedSizeSrc || loadFromFilePathOptions?.filePath,
	});
	const isImageLoaded = croppedSizeSrc || loadFromFilePathOptions?.filePath;

	useEffect(() => {
		const getPreSignedUrl = async () => {
			try {
				setLoading(true);
				const response = await getActualFilePath(loadFromFilePathOptions?.filePath || '');
				setActualPreSignedUrl(response.data.preSignedUrl);
			} catch (err) {
				const error = err instanceof Error ? err : new Error(String(err));
				const appError = createAppError(error);
				addAppError(appError);
			} finally {
				setLoading(false);
			}
		};

		if (loadFromFilePathOptions?.preSignedUrl) {
			setActualPreSignedUrl(loadFromFilePathOptions.preSignedUrl);
		} else if (!loadFromFilePathOptions?.preSignedUrl && loadFromFilePathOptions?.filePath) {
			getPreSignedUrl();
		}
	}, [loadFromFilePathOptions?.preSignedUrl, loadFromFilePathOptions?.filePath]);

	const deletePhoto = () => {
		setPendingImage({ file: null, src: null });
		setPendingGif({ file: null, src: null });
		setCroppedSizeSrc(null);
		setLoadingImageError(false);
		deletePhotoHandler();
	};

	// Clear img src after form reset
	useEffect(() => {
		if (!fileId && !loadFromFilePathOptions) {
			deletePhoto();
		}
	}, [fileId]);

	const openCropper = () => {
		changeTypeOfModalWindow('cropper');
	};

	const showErrorImage = () => {
		setLoadingImageError(true);
	};

	const renderImage = () => {
		if (isLoading) {
			return (
				<div className={style.imageContainer}>
					<LoaderWithProps loaderSize="big" />;
				</div>
			);
		}

		if (loadingImageError) {
			return (
				<div className={style.brokenImageContainer}>
					<svg className={style.brokenImageIcon}>
						<use xlinkHref={`${breadcrumbs}#brokenPicture`} />
					</svg>
					<div>Error image</div>
				</div>
			);
		}

		if (croppedSizeSrc) {
			return (
				<div className={style.imageContainer}>
					<img className={style.image} src={croppedSizeSrc} alt="Photo" />
				</div>
			);
		}

		if (!croppedSizeSrc && !loadFromFilePathOptions?.filePath) {
			return (
				<>
					<div className={style.noImageContainer}>
						<svg className={style.noImageIcon}>
							<use xlinkHref={`${breadcrumbs}#${noImageIconId}`} />
						</svg>
					</div>
					{withValidation && isShowValidationError && (
						<Alert variant="error" className="mt-2">
							{validationErrorMessage}
						</Alert>
					)}
				</>
			);
		}

		if (actualPreSignedUrl && !croppedSizeSrc) {
			return (
				<div className={style.imageContainer}>
					<img className={style.image} src={actualPreSignedUrl} alt="Photo" onError={showErrorImage} />
				</div>
			);
		}
	};

	const hasAbilityToUploadGif = !!maxFileSizeMB.gif;
	const fileSizeMB = maxFileSizeMB.gif || maxFileSizeMB.img;
	const uploadButtonTranslationKey = isImageLoaded
		? (hasAbilityToUploadGif && 'replaceFileButton') || 'replacePhotoButton'
		: (hasAbilityToUploadGif && 'uploadFileButton') || 'uploadPhotoButton';
	const deleteButtonTranslationKey = hasAbilityToUploadGif ? 'deleteFileButton' : 'deletePhotoButton';

	return (
		<div className={containerClasses} ref={containerRef} data-test="FormImageUploaderNew">
			<div className={style.renderImageWrapper}>
				{renderImage()}
				{infoContent && <div className={style.absoluteContent}>{infoContent}</div>}
			</div>
			<FileSizeLabel maxFileSizeMB={fileSizeMB} />
			<div className={style.buttonsWrapper}>
				<Button
					className={style.button}
					icon={
						<svg className={style.icon}>
							<use xlinkHref={`${breadcrumbs}#${!isImageLoaded ? 'uploadPhoto' : 'reload'}`} />
						</svg>
					}
					onClick={openCropper}>
					<Text translationNameSpace="form" translationKey={uploadButtonTranslationKey} asFragment={true} />
				</Button>
				<Button
					className={deleteButtonClasses}
					theme="secondary"
					icon={
						<svg className={deleteIconClasses}>
							<use xlinkHref={`${breadcrumbs}#bin`} />
						</svg>
					}
					onClick={deletePhoto}>
					<Text translationNameSpace="form" translationKey={deleteButtonTranslationKey} asFragment={true} />
				</Button>
			</div>
		</div>
	);
};
